import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import {
  FILE_FOLDERS,
  fileMatchesFolder,
  isImageFile,
  LANG_FLAGS,
  LANG_LABELS,
  mapFileRow,
  TRANSLATE_LANG_OPTIONS,
  TRANSLATION_STATUS,
} from './adminToolShared'
import { RsIcon, resolveIconNode, IconLabel, ModalClose } from '../../../components/icons'
import { useConfirm } from '../../../contexts/ConfirmContext'
import TranslationViewer from '../../../components/TranslationViewer'

const FILE_TABS = ['Folders', 'All Files', 'Shared', 'Recent', 'Translated', 'Confidential']

function FileTranslationBadges({ file, onView }) {
  const langs = Object.keys(file.translations || {})
  if (!langs.length) {
    return <span className="admin-file-tr-empty">—</span>
  }
  return (
    <div className="admin-file-tr-badges">
      {langs.map((lang) => {
        const status = file.translations[lang]
        return (
          <button
            key={lang}
            type="button"
            className={`admin-file-tr-badge ${TRANSLATION_STATUS[status]?.color || 'pending'}`}
            title={`${LANG_LABELS[lang] || lang} — ${TRANSLATION_STATUS[status]?.label || status}`}
            onClick={(e) => { e.stopPropagation(); onView?.(file, lang) }}
          >
            {LANG_FLAGS[lang] || lang}
          </button>
        )
      })}
      <button type="button" className="admin-file-tr-view" onClick={(e) => { e.stopPropagation(); onView?.(file) }}>
        View translation
      </button>
    </div>
  )
}

function FileActions({ file, onDownload, onPreview, onTranslate, onDelete, deleting }) {
  return (
    <div className="admin-file-actions admin-row-actions" data-file-id={file.id} onClick={(e) => e.stopPropagation()}>
      {isImageFile(file) && (
        <button type="button" className="admin-row-action-btn" data-testid="file-preview-btn" onClick={() => onPreview(file)} title="Preview">
          <RsIcon name="expand" size="xs" />
          <span>Preview</span>
        </button>
      )}
      <button type="button" className="admin-row-action-btn" data-testid="file-download-btn" onClick={() => onDownload(file)} title="Download">
        <RsIcon name="download" size="xs" />
        <span>Download</span>
      </button>
      <button type="button" className="admin-row-action-btn" data-testid="file-translate-btn" onClick={() => onTranslate(file)} title="Translate">
        <RsIcon name="translate" size="xs" />
        <span>Translate</span>
      </button>
      <button
        type="button"
        className="admin-row-action-btn danger"
        data-testid="file-delete-btn"
        onClick={() => onDelete(file)}
        disabled={deleting}
        title="Delete"
      >
        <RsIcon name="trash" size="xs" />
        <span>Delete</span>
      </button>
    </div>
  )
}

export default function AdminFilesView() {
  const confirm = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const shareOpenedRef = useRef(null)
  const [files, setFiles] = useState([])
  const [deals, setDeals] = useState([])
  const [tab, setTab] = useState('Folders')
  const [view, setView] = useState('grid')
  const [search, setSearch] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [showTranslate, setShowTranslate] = useState(false)
  const [viewTranslations, setViewTranslations] = useState(null)
  const [viewTranslationLang, setViewTranslationLang] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [translateLangs, setTranslateLangs] = useState(['en', 'zh'])
  const [folderFilter, setFolderFilter] = useState(null)
  const [storage, setStorage] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [translating, setTranslating] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadDealId, setUploadDealId] = useState('')
  const [previewImage, setPreviewImage] = useState(null)
  const fileInputRef = useRef(null)
  const previewUrlRef = useRef(null)

  const loadStorage = useCallback(async () => {
    try {
      const res = await axios.get('/api/files/storage-usage')
      setStorage(res.data)
    } catch {
      setStorage(null)
    }
  }, [])

  const loadFiles = useCallback(async () => {
    try {
      const res = await axios.get('/api/files')
      setFiles(res.data.map(mapFileRow))
    } catch {
      toast.error('Failed to load files')
    }
  }, [])

  const handleDownload = useCallback(async (file) => {
    try {
      const url = file.url || `/api/files/download/${file.id}`
      const res = await axios.get(url, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = file.name || 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)
      toast.success(`Downloaded ${file.name}`)
      return true
    } catch {
      toast.error('Download failed')
      return false
    }
  }, [])

  const closePreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
    setPreviewImage(null)
  }, [])

  const handlePreview = useCallback(async (file) => {
    if (!isImageFile(file)) return
    try {
      const url = file.url || `/api/files/download/${file.id}`
      const res = await axios.get(url, { responseType: 'blob' })
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current)
      }
      const blobUrl = window.URL.createObjectURL(res.data)
      previewUrlRef.current = blobUrl
      setPreviewImage({ file, url: blobUrl })
    } catch {
      toast.error('Preview failed')
    }
  }, [])

  useEffect(() => {
    loadFiles()
    loadStorage()
    axios.get('/api/deals/my-deals').then((r) => setDeals(r.data || [])).catch(() => {})
  }, [loadFiles, loadStorage])

  useEffect(() => () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current)
    }
  }, [])

  useEffect(() => {
    const fileId = searchParams.get('file')
    if (!fileId || !files.length) return
    if (shareOpenedRef.current === fileId) return

    const file = files.find((f) => f.id === fileId)
    if (!file) {
      toast.error('File not found or you do not have access')
      const next = new URLSearchParams(searchParams)
      next.delete('file')
      setSearchParams(next, { replace: true })
      return
    }

    shareOpenedRef.current = fileId
    setTab('All Files')
    setView('list')
    const openAction = isImageFile(file) ? handlePreview(file) : handleDownload(file)
    openAction.finally(() => {
      const next = new URLSearchParams(searchParams)
      next.delete('file')
      setSearchParams(next, { replace: true })
    })
  }, [files, searchParams, setSearchParams, handleDownload, handlePreview])

  const filtered = useMemo(() => {
    let list = [...files]
    if (search) {
      const s = search.toLowerCase()
      list = list.filter((f) =>
        f.name.toLowerCase().includes(s) || String(f.deal || '').toLowerCase().includes(s)
      )
    }
    if (folderFilter) {
      list = list.filter((f) => fileMatchesFolder(f.name, folderFilter))
    }
    if (tab === 'Shared') list = list.filter((f) => f.deal)
    if (tab === 'Recent') list.sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''))
    if (tab === 'Translated') list = list.filter((f) => Object.keys(f.translations || {}).length > 0)
    if (tab === 'Confidential') list = list.filter((f) => f.isConfidential)
    return list
  }, [files, search, folderFilter, tab])

  const totalBytes = storage?.usedBytes ?? files.reduce((s, f) => s + (f.sizeBytes || 0), 0)
  const limitGb = storage?.limitGb ?? 50
  const usedGb = totalBytes / (1024 ** 3)
  const usedLabel = usedGb >= 0.1
    ? `${usedGb.toFixed(1)} GB`
    : totalBytes >= 1024 * 1024
      ? `${(totalBytes / (1024 ** 2)).toFixed(1)} MB`
      : `${(totalBytes / 1024).toFixed(1)} KB`
  const pct = storage?.percentUsed ?? Math.min(100, Math.round((totalBytes / (limitGb * 1024 ** 3)) * 100))

  const openTranslationViewer = useCallback((file, lang) => {
    setViewTranslations(file)
    setViewTranslationLang(lang || null)
  }, [])

  const closeTranslationViewer = useCallback(() => {
    setViewTranslations(null)
    setViewTranslationLang(null)
  }, [])

  const openTranslate = (file) => {
    setSelectedFile(file)
    setShowTranslate(true)
  }

  const handleDelete = async (file) => {
    const ok = await confirm({
      title: 'Delete file?',
      message: `Delete "${file.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    setDeletingId(file.id)
    try {
      await axios.delete(`/api/files/${file.id}`)
      toast.success('File deleted')
      if (viewTranslations?.id === file.id) closeTranslationViewer()
      await loadFiles()
      await loadStorage()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    } finally {
      setDeletingId(null)
    }
  }

  const handleTranslate = async () => {
    if (!selectedFile) return
    setTranslating(true)
    try {
      const res = await axios.post('/api/files/translate', {
        fileId: selectedFile.id,
        targetLanguages: translateLangs,
      })
      const { translations: texts, translationMeta, okCount, source, sourceCharCount } = res.data
      const okLangs = Object.entries(translationMeta || {})
        .filter(([, s]) => s === 'ok')
        .map(([l]) => LANG_LABELS[l] || l)
      const failLangs = Object.entries(translationMeta || {})
        .filter(([, s]) => s === 'failed')
        .map(([l]) => LANG_LABELS[l] || l)

      if (okCount === 0) {
        toast.error(res.data.message || 'Translation failed', { duration: 6000 })
      } else if (failLangs.length) {
        toast.success(`Translated: ${okLangs.join(', ')}; failed: ${failLangs.join(', ')}`)
      } else {
        const srcNote =
          source === 'pdf_content'
            ? `PDF body (~${sourceCharCount || '?'} chars)`
            : source === 'word_content'
              ? `Word body (~${sourceCharCount || '?'} chars)`
            : source === 'file_content'
              ? 'document body'
              : 'filename summary'
        toast.success(`Translated to ${okLangs.join(', ')} (${srcNote})`)
      }

      await loadFiles()
      setShowTranslate(false)
      if (okCount > 0 && selectedFile) {
        const merged = { ...selectedFile }
        merged.translationTexts = texts
        merged.translations = {}
        Object.entries(texts || {}).forEach(([lang, text]) => {
          merged.translations[lang] = /^【/.test(String(text).trim()) ? 'failed' : 'ready'
        })
        openTranslationViewer(
          merged,
          Object.entries(translationMeta || {}).find(([, s]) => s === 'ok')?.[0] || null,
        )
      }
      setSelectedFile(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Translation failed')
    } finally {
      setTranslating(false)
    }
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadProgress(0)
    const formData = new FormData()
    formData.append('file', file)
    if (uploadDealId) formData.append('dealId', uploadDealId)
    formData.append('isConfidential', 'false')
    try {
      await axios.post('/api/files/upload', formData, {
        onUploadProgress: (ev) => {
          if (ev.total) {
            setUploadProgress(Math.round((ev.loaded * 100) / ev.total))
          }
        },
      })
      toast.success('File uploaded successfully!')
      setShowUpload(false)
      setUploadProgress(0)
      setTab('All Files')
      setView('list')
      setFolderFilter(null)
      await loadFiles()
      await loadStorage()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed')
      setUploadProgress(0)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const openFolder = (folderId) => {
    setFolderFilter(folderId)
    setTab('All Files')
    setView('list')
  }

  const folderCount = (folderId) => files.filter((f) => fileMatchesFolder(f.name, folderId)).length

  const showFolderGrid = tab === 'Folders' && view === 'grid'
  const showFileGrid = !showFolderGrid && view === 'grid'
  const showFileTable = !showFolderGrid && !showFileGrid

  return (
    <section className="admin-tool-files">
      <header className="admin-tool-topbar">
        <div className="admin-tool-topbar-left">
          <span className="admin-tool-topbar-title">Files</span>
          <span className="admin-tool-topbar-sub">Secure storage · multi-language translation (DeepSeek)</span>
        </div>
      </header>

      <div className="admin-files-body">
        <div className="admin-files-storage">
          <div className="admin-files-storage-info">
            <div className="admin-files-storage-label">Storage: {usedLabel} / {limitGb} GB used</div>
            <div className="admin-files-storage-track">
              <div className="admin-files-storage-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="admin-files-storage-detail">{(Math.max(0, limitGb - usedGb)).toFixed(1)} GB available</div>
          </div>
        </div>

        <div className="admin-files-tabs">
          {FILE_TABS.map((t) => (
            <button
              key={t}
              type="button"
              className={`admin-files-tab${tab === t ? ' active' : ''}`}
              onClick={() => { setTab(t); if (t === 'Folders') setFolderFilter(null) }}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="admin-files-toolbar">
          <div className="admin-tool-search">
            <RsIcon name="search" size="sm" aria-hidden />
            <input placeholder="Search files, folders..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="admin-files-view-toggle">
            <button type="button" className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} aria-label="Grid view">
              <RsIcon name="gridView" size="sm" />
            </button>
            <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} aria-label="List view">
              <RsIcon name="listView" size="sm" />
            </button>
          </div>
          <button type="button" className="admin-btn primary" onClick={() => setShowUpload(true)}>
            <IconLabel name="upload" size="xs">Upload</IconLabel>
          </button>
        </div>

        <div className="admin-files-breadcrumb">
          <button type="button" className="admin-files-crumb-btn" onClick={() => { setTab('Folders'); setFolderFilter(null) }}>Files</button>
          <span>›</span>
          <span className="current">{folderFilter ? FILE_FOLDERS.find((f) => f.id === folderFilter)?.name : (tab === 'Folders' ? 'All Folders' : tab)}</span>
        </div>

        {showFolderGrid && (
          <div className="admin-folder-grid">
            {FILE_FOLDERS.map((f) => (
              <button key={f.id} type="button" className="admin-folder-card" onClick={() => openFolder(f.id)}>
                <div className={`admin-folder-icon ${f.cls}`}>{resolveIconNode(f.icon, { size: 'md' })}</div>
                <div className="admin-folder-name">{f.name}</div>
                <div className="admin-folder-count">{folderCount(f.id)} files</div>
                {f.badge && folderCount(f.id) > 0 && <span className={`admin-folder-badge ${f.badge}`}>{f.badgeText}</span>}
              </button>
            ))}
          </div>
        )}

        {showFileGrid && (
          <div className="admin-file-card-grid">
            {filtered.length === 0 ? (
              <p className="admin-files-empty">No files found</p>
            ) : filtered.map((f) => (
              <article key={f.id} className="admin-file-card">
                <div className="admin-file-card-head">
                  <div className={`admin-file-type-icon ${f.type}`}>
                    <RsIcon name={f.type === 'png' ? 'image' : 'fileText'} size="md" />
                  </div>
                  <div className="admin-file-card-meta">
                    <button
                      type="button"
                      className={`admin-file-name${isImageFile(f) ? ' admin-file-name-previewable' : ''}`}
                      title={f.name}
                      onClick={isImageFile(f) ? () => handlePreview(f) : undefined}
                    >
                      {f.name}
                    </button>
                    <div className="admin-file-card-sub">{f.size} · {f.uploadedAt}</div>
                  </div>
                  {f.isConfidential && <span className="admin-file-tag confidential">Confidential</span>}
                </div>
                {f.deal && <p className="admin-file-card-deal">Deal: {f.deal}</p>}
                <FileTranslationBadges file={f} onView={openTranslationViewer} />
                <FileActions file={f} onDownload={handleDownload} onPreview={handlePreview} onTranslate={openTranslate} onDelete={handleDelete} deleting={deletingId === f.id} />
              </article>
            ))}
          </div>
        )}

        {showFileTable && (
          <div className="admin-file-table">
            <div className="admin-file-table-header">
              <span /><span>Name</span><span>Type</span><span>Size</span><span>Modified</span><span>Translations</span><span>Actions</span>
            </div>
            {filtered.length === 0 ? (
              <p className="admin-files-empty">No files found</p>
            ) : filtered.map((f) => (
              <div key={f.id} className="admin-file-row">
                <div className={`admin-file-type-icon ${f.type}`}>
                  <RsIcon name={f.type === 'png' ? 'image' : 'fileText'} size="md" />
                </div>
                <div className="admin-file-name-cell">
                  <button
                    type="button"
                    className={`admin-file-name${isImageFile(f) ? ' admin-file-name-previewable' : ''}`}
                    title={f.name}
                    onClick={isImageFile(f) ? () => handlePreview(f) : undefined}
                  >
                    {f.name}
                    {f.isConfidential && <span className="admin-file-tag confidential">Confidential</span>}
                  </button>
                </div>
                <span>{f.type.toUpperCase()}</span>
                <span>{f.size}</span>
                <span className="admin-file-col admin-file-col-modified">{f.uploadedAt}</span>
                <div className="admin-file-col admin-file-col-translations">
                  <FileTranslationBadges file={f} onView={openTranslationViewer} />
                </div>
                <div className="admin-file-col admin-file-col-actions">
                  <FileActions file={f} onDownload={handleDownload} onPreview={handlePreview} onTranslate={openTranslate} onDelete={handleDelete} deleting={deletingId === f.id} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showUpload && (
        <div className="admin-modal-overlay show" onClick={() => !uploading && setShowUpload(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Upload File</h3>
              <ModalClose onClick={() => setShowUpload(false)} disabled={uploading} />
            </div>
            <div className="admin-modal-body">
              {deals.length > 0 && (
                <div className="field">
                  <label htmlFor="upload-deal">Link to Deal (optional)</label>
                  <select id="upload-deal" value={uploadDealId} onChange={(e) => setUploadDealId(e.target.value)}>
                    <option value="">None</option>
                    {deals.map((d) => (
                      <option key={d.id} value={d.id}>{d.title}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="admin-upload-zone" style={{ display: 'block', cursor: uploading ? 'wait' : 'pointer' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleUpload}
                  disabled={uploading}
                />
                <div className="uz-icon"><RsIcon name="folder" size="lg" /></div>
                <p>{uploading ? `Uploading… ${uploadProgress}%` : 'Click to browse or drop a file here'}</p>
                <p style={{ fontSize: 12, color: '#909399', marginTop: 8 }}>PDF, Office, images, archives, and other common formats (max 100MB)</p>
              </label>
              {uploading && (
                <div className="admin-upload-progress">
                  <div className="admin-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="admin-modal-overlay show" onClick={closePreview}>
          <div className="admin-modal admin-modal-lg admin-file-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div>
                <h3>Image Preview</h3>
                <p className="admin-modal-subtitle">{previewImage.file.name}</p>
              </div>
              <ModalClose onClick={closePreview} />
            </div>
            <div className="admin-modal-body admin-file-preview-body">
              <img src={previewImage.url} alt={previewImage.file.name} className="admin-file-preview-img" />
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={closePreview}>Close</button>
              <button type="button" className="admin-btn primary" onClick={() => handleDownload(previewImage.file)}>
                <IconLabel name="download" size="xs">Download</IconLabel>
              </button>
            </div>
          </div>
        </div>
      )}

      {viewTranslations && (
        <TranslationViewer
          file={viewTranslations}
          initialLang={viewTranslationLang}
          onClose={closeTranslationViewer}
        />
      )}

      {showTranslate && selectedFile && (
        <div className="admin-modal-overlay show" onClick={() => !translating && (setShowTranslate(false), setSelectedFile(null))}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Translate Document</h3>
              <ModalClose onClick={() => { setShowTranslate(false); setSelectedFile(null) }} disabled={translating} />
            </div>
            <div className="admin-modal-body">
              <p style={{ fontSize: 13, color: '#606266', marginBottom: 16 }}>{selectedFile.name}</p>
              <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Select target languages:</p>
              <div className="admin-translate-lang-list">
                {TRANSLATE_LANG_OPTIONS.map(({ code, label, flag }) => (
                  <label key={code} className="admin-translate-lang-item">
                    <input
                      type="checkbox"
                      checked={translateLangs.includes(code)}
                      onChange={(e) => {
                        if (e.target.checked) setTranslateLangs((prev) => [...prev, code])
                        else setTranslateLangs((prev) => prev.filter((l) => l !== code))
                      }}
                    />
                    <span>{flag}</span>
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="admin-settings-alert info" style={{ marginTop: 16 }}>
                <RsIcon name="info" size="md" />
                <span><strong>Powered by DeepSeek</strong> — PDF body parsing and multi-language translation; scanned PDFs require OCR</span>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => { setShowTranslate(false); setSelectedFile(null) }} disabled={translating}>Cancel</button>
              <button type="button" className="admin-btn primary" onClick={handleTranslate} disabled={translateLangs.length === 0 || translating}>
                {translating ? 'Translating…' : 'Start Translation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
